import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Check, Upload, X, Plus, Loader2, AlertCircle, Play, CheckCircle2, Circle, Info, ChevronLeft } from 'lucide-react';
import { injectVctStyles, ConceptSampleModal, ConceptManualModal, CONCEPT_TEMPLATE } from '../../simulationUseCase/useCaseForms/ConceptCardShared';
import { useAuth } from '../../../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || '';
const BACCHUS_ALLOWED_DOMAINS = (import.meta.env.VITE_BACCHUS_ALLOWED_DOMAINS || '').split(',').map((d: string) => d.trim().toLowerCase()).filter(Boolean);

// ── Backend types ────────────────────────────────────────────────────────────

export interface BackendPlanStep {
  id: string;
  label: string;
  status: 'done' | 'active' | 'pending';
  value: any;
  collected_inputs?: Record<string, any>;
}

export interface BackendPlan {
  use_case: string;
  label: string;
  steps: BackendPlanStep[];
}

export interface BackendFormFieldOption {
  id: string;
  name: string;
  select_all?: boolean;
  persona_count?: number;
  description?: string;
}

export interface BackendFormField {
  id: string;
  type: 'text_input' | 'textarea' | 'text_list' | 'image_upload' | 'image_list' | 'segment_select' | 'audience_select' | 'concept_card';
  label: string;
  required: boolean;
  value: any;
  placeholder?: string;
  options?: BackendFormFieldOption[];
  min_count?: number;
  max_count?: number;
  initial_rows?: number;
}

export interface BackendForm {
  step_id: string;
  title: string;
  subtitle: string;
  step_number: number;
  total_steps: number;
  fields: BackendFormField[];
}

export type BackendMessageType =
  | 'twin_chat'
  | 'intent_confirmation'
  | 'intent_clarification'
  | 'specialist_collecting'
  | 'specialist_ready'
  | 'simulation_dispatching';

export interface FormResponse {
  success: boolean;
  type: BackendMessageType;
  response: string | null;
  use_case?: string;
  missing_fields?: string[];
  plan: BackendPlan | null;
  form: BackendForm | null;
  simulation_id?: number | null;
  active_step_id?: string | null;
}

// ── Image helpers ────────────────────────────────────────────────────────────

interface ImageSlot {
  id: string;
  file: File;
  preview: string;
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function isBase64Image(v: string) {
  return typeof v === 'string' && v.startsWith('data:image');
}

function isImageUrl(v: string) {
  return typeof v === 'string' && (v.startsWith('http') || v.startsWith('/'));
}

function resolveImageSrc(v: string): string {
  if (isBase64Image(v)) return v;
  if (v.startsWith('http')) return v;
  return `${API_URL}${v.startsWith('/') ? '' : '/'}${v}`;
}

// ── Value types ──────────────────────────────────────────────────────────────

type FieldValue = string | string[] | ImageSlot | ImageSlot[] | null;

// ── Plan card ────────────────────────────────────────────────────────────────

// Renders a stored (backend) step value — text, image, or array of either
const StepValuePreview: React.FC<{ value: any }> = ({ value }) => {
  if (value == null || value === '') return null;

  if (Array.isArray(value)) {
    const imgItems = (value as string[]).filter(v => typeof v === 'string' && (isBase64Image(v) || isImageUrl(v)));
    if (imgItems.length > 0) {
      return (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {imgItems.map((src, i) => (
            <img key={i} src={resolveImageSrc(src)} alt="" className="h-12 w-12 object-cover rounded-lg border border-gray-200" />
          ))}
        </div>
      );
    }
    const text = (value as string[]).filter(Boolean).join(', ');
    return text ? <p className="text-[11px] text-gray-400 mt-0.5">{text}</p> : null;
  }

  const v = value as string;
  if (isBase64Image(v) || isImageUrl(v)) {
    return <img src={resolveImageSrc(v)} alt="" className="h-12 w-12 object-cover rounded-lg border border-gray-200 mt-1.5" />;
  }

  return <p className="text-[11px] text-gray-400 mt-0.5 break-words">{v}</p>;
};

// Renders live field values while the user is filling the active step
const LiveFieldsPreview: React.FC<{
  fields: BackendFormField[];
  values: Record<string, FieldValue>;
}> = ({ fields, values }) => {
  const items: React.ReactNode[] = [];

  for (const field of fields) {
    const v = values[field.id];
    if (v === null || v === undefined) continue;

    if (field.type === 'image_upload') {
      const slot = v as ImageSlot | null;
      if (!slot) continue;
      items.push(
        <img key={field.id} src={slot.preview} alt={field.label} className="h-12 w-12 object-cover rounded-lg border border-gray-200" />
      );
    } else if (field.type === 'image_list') {
      const slots = v as ImageSlot[];
      if (!slots.length) continue;
      items.push(
        <div key={field.id} className="flex flex-wrap gap-1.5">
          {slots.map(s => (
            <img key={s.id} src={s.preview} alt="" className="h-12 w-12 object-cover rounded-lg border border-gray-200" />
          ))}
        </div>
      );
    } else if (field.type === 'text_list') {
      const rows = (v as string[]).filter(r => r.trim());
      if (!rows.length) continue;
      items.push(
        <p key={field.id} className="text-[11px] text-gray-400">{rows.join(', ')}</p>
      );
    } else if (field.type === 'segment_select') {
      const ids = v as string[];
      if (!ids.length) continue;
      const names = ids.map(id => field.options?.find(o => String(o.id) === String(id))?.name ?? id).join(', ');
      items.push(
        <p key={field.id} className="text-[11px] text-gray-400">{names}</p>
      );
    } else {
      const text = (v as string)?.trim();
      if (!text) continue;
      items.push(
        <p key={field.id} className="text-[11px] text-gray-400 break-words">{text}</p>
      );
    }
  }

  if (!items.length) return null;
  return <div className="mt-1.5 space-y-1.5">{items}</div>;
};

interface PlanCardProps {
  plan: BackendPlan;
  activeStepId?: string | null;
  activePreview?: { fields: BackendFormField[]; values: Record<string, FieldValue> };
}

export const PlanCard: React.FC<PlanCardProps> = ({ plan, activeStepId, activePreview }) => {
  const activeRowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeStepId]);

  return (
    <ul className="space-y-2 mb-2.5">
      {plan.steps.map((step) => {
        const isActive = activeStepId != null ? step.id === activeStepId : step.status === 'active';
        const isDone = !isActive && step.status === 'done';
        const HIDDEN_COLLECTED_KEYS = new Set(['audience_id', 'segment_ids']);
        const collectedEntries = step.collected_inputs
          ? Object.entries(step.collected_inputs).filter(([k, v]) => !HIDDEN_COLLECTED_KEYS.has(k) && v != null && v !== '' && !(Array.isArray(v) && v.length === 0))
          : null;
        return (
          <li
            key={step.id}
            ref={isActive ? activeRowRef : undefined}
            className={`transition-all duration-300 ${
              isActive ? 'bg-[#E8FCFA] rounded-lg p-3 -mx-1 border border-[#07E5D1]/20' : 'px-1'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {isDone && <CheckCircle2 className="w-5 h-5 text-[#07E5D1]" />}
                {isActive && (
                  <div className="relative">
                    <Circle className="w-5 h-5 text-[#07E5D1]" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-2 bg-[#07E5D1] rounded-full animate-ping" />
                    </div>
                  </div>
                )}
                {!isDone && !isActive && <Circle className="w-5 h-5 text-gray-300" />}
              </div>
              <div className="flex-1 min-w-0">
                <span className={`text-sm leading-tight block ${
                  isDone   ? 'text-gray-400' :
                  isActive ? 'text-gray-800 font-medium' :
                             'text-gray-400'
                }`}>
                  {step.label}
                </span>
                {isDone && collectedEntries
                  ? <div className="space-y-0.5 mt-0.5">{collectedEntries.map(([k, v]) => <StepValuePreview key={k} value={v} />)}</div>
                  : isDone && <StepValuePreview value={step.value} />
                }
                {isActive && activePreview && (
                  <LiveFieldsPreview fields={activePreview.fields} values={activePreview.values} />
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

// ── Field label ──────────────────────────────────────────────────────────────

const FieldLabel: React.FC<{ field: BackendFormField }> = ({ field }) => (
  <div className="flex items-center gap-1.5 mb-1.5">
    <span className="text-xs font-medium text-gray-700">{field.label}</span>
    {field.required
      ? <span className="text-red-500 text-xs">*</span>
      : <span className="text-[11px] text-gray-400">(optional)</span>
    }
  </div>
);

// ── Field renderers ───────────────────────────────────────────────────────────

const TextInputField: React.FC<{
  field: BackendFormField; value: string; onChange: (v: string) => void;
}> = ({ field, value, onChange }) => (
  <div>
    <FieldLabel field={field} />
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={field.placeholder || ''}
      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#07E5D1]/30 focus:border-[#07E5D1]"
    />
  </div>
);

const TextareaField: React.FC<{
  field: BackendFormField; value: string; onChange: (v: string) => void;
}> = ({ field, value, onChange }) => (
  <div>
    <FieldLabel field={field} />
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={field.placeholder || ''}
      rows={4}
      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#07E5D1]/30 focus:border-[#07E5D1] resize-none"
    />
  </div>
);

const TextListField: React.FC<{
  field: BackendFormField; value: string[]; onChange: (v: string[]) => void;
}> = ({ field, value, onChange }) => {
  const minCount = field.min_count ?? 1;
  const maxCount = field.max_count ?? Infinity;
  const initialRows = field.initial_rows ?? 1;
  const rows = value.length > 0 ? value : Array(initialRows).fill('');
  const filledCount = rows.filter(r => r.trim()).length;
  const updateRow = (idx: number, v: string) => { const next = [...rows]; next[idx] = v; onChange(next); };
  const addRow = () => { if (rows.length < maxCount) onChange([...rows, '']); };
  const removeRow = (idx: number) => { const next = rows.filter((_, i) => i !== idx); onChange(next.length ? next : ['']); };
  const belowMin = field.required && filledCount < minCount;

  return (
    <div>
      <FieldLabel field={field} />
      <div className="space-y-2">
        {rows.map((row, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <span className="text-xs text-gray-400 w-5 text-right flex-shrink-0">{idx + 1}.</span>
            <input
              type="text"
              value={row}
              onChange={e => updateRow(idx, e.target.value)}
              placeholder={field.placeholder || `Item ${idx + 1}`}
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#07E5D1]/30 focus:border-[#07E5D1]"
            />
            {rows.length > 1 && (
              <button onClick={() => removeRow(idx)} className="text-gray-400 hover:text-red-500 transition-colors">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
        <div className="flex items-center justify-between mt-1">
          {rows.length < maxCount ? (
            <button onClick={addRow} className="flex items-center gap-1.5 text-xs text-[#07E5D1] hover:text-[#06C4B4] transition-colors">
              <Plus className="w-3.5 h-3.5" /> Add item
            </button>
          ) : (
            <span className="text-xs text-gray-400">Maximum {maxCount} items</span>
          )}
          {belowMin && (
            <span className="text-xs text-amber-500">{filledCount}/{minCount} required</span>
          )}
          {!belowMin && field.min_count && (
            <span className="text-xs text-gray-400">{filledCount} {maxCount < Infinity ? `/ ${maxCount}` : ''}</span>
          )}
        </div>
      </div>
    </div>
  );
};

const ImageUploadField: React.FC<{
  field: BackendFormField; value: ImageSlot | null; onChange: (v: ImageSlot | null) => void;
}> = ({ field, value, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (value) URL.revokeObjectURL(value.preview);
    onChange({ id: `${Date.now()}`, file, preview: URL.createObjectURL(file) });
    e.target.value = '';
  };

  return (
    <div>
      <FieldLabel field={field} />
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      {value ? (
        <div className="relative w-32 h-32 rounded-xl overflow-hidden border border-gray-200">
          <img src={value.preview} alt="" className="w-full h-full object-cover" />
          <button
            onClick={() => { URL.revokeObjectURL(value.preview); onChange(null); }}
            className="absolute top-1 right-1 bg-black/50 rounded-full p-0.5 text-white hover:bg-black/70"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-[#07E5D1] transition-colors"
        >
          <Upload className="w-5 h-5 text-gray-400 mb-1" />
          <span className="text-[10px] text-gray-400 text-center px-1">{field.label}</span>
        </div>
      )}
    </div>
  );
};

const ImageListField: React.FC<{
  field: BackendFormField; value: ImageSlot[]; onChange: (v: ImageSlot[]) => void;
}> = ({ field, value, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newSlots: ImageSlot[] = files.map(f => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file: f,
      preview: URL.createObjectURL(f),
    }));
    onChange([...value, ...newSlots]);
    e.target.value = '';
  };
  const remove = (id: string) => {
    const slot = value.find(s => s.id === id);
    if (slot) URL.revokeObjectURL(slot.preview);
    onChange(value.filter(s => s.id !== id));
  };

  return (
    <div>
      <FieldLabel field={field} />
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} />
      <div className="grid grid-cols-3 gap-2">
        {value.map(slot => (
          <div key={slot.id} className="relative aspect-square rounded-xl overflow-hidden border border-gray-200">
            <img src={slot.preview} alt="" className="w-full h-full object-cover" />
            <button
              onClick={() => remove(slot.id)}
              className="absolute top-1 right-1 bg-black/50 rounded-full p-0.5 text-white hover:bg-black/70"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button
          onClick={() => inputRef.current?.click()}
          className="aspect-square border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center hover:border-[#07E5D1] transition-colors cursor-pointer"
        >
          <Plus className="w-5 h-5 text-gray-400" />
          <span className="text-[10px] text-gray-400 mt-0.5">Add</span>
        </button>
      </div>
    </div>
  );
};

const SegmentSelectField: React.FC<{
  field: BackendFormField; value: string[]; onChange: (v: string[]) => void;
}> = ({ field, value, onChange }) => {
  const options = field.options || [];
  const toggle = (id: string, selectAll?: boolean) => {
    if (selectAll) {
      const allIds = options.filter(o => !o.select_all).map(o => o.id);
      onChange(allIds.every(id => value.includes(id)) ? [] : allIds);
      return;
    }
    onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);
  };

  if (options.length === 0) return (
    <div><FieldLabel field={field} /><p className="text-xs text-gray-400 italic">No options available.</p></div>
  );

  return (
    <div>
      <FieldLabel field={field} />
      <div className="space-y-1.5">
        {options.map(opt => {
          const allNonSelectAll = options.filter(o => !o.select_all).map(o => o.id);
          const isChecked = opt.select_all
            ? allNonSelectAll.every(id => value.includes(id))
            : value.includes(opt.id);
          return (
            <label
              key={opt.id}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                isChecked ? 'border-[#07E5D1] bg-[#F0FDFA]' : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className={`w-4 h-4 rounded flex items-center justify-center border-2 flex-shrink-0 ${
                isChecked ? 'border-[#07E5D1] bg-[#07E5D1]' : 'border-gray-300'
              }`}>
                {isChecked && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
              </div>
              <span className={`text-sm flex-1 ${isChecked ? 'text-[#1C1C1E] font-medium' : 'text-gray-700'}`}>
                {opt.name}
                {!opt.select_all && opt.persona_count != null && (
                  <span className="ml-1.5 text-xs text-gray-400 font-normal">({opt.persona_count})</span>
                )}
              </span>
              {!opt.select_all && opt.description && (
                <div className="relative group flex-shrink-0" onClick={e => e.preventDefault()}>
                  <Info className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-default" />
                  <div className="absolute right-0 bottom-5 hidden group-hover:block z-10 w-56 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg leading-relaxed pointer-events-none">
                    {opt.description}
                    <div className="absolute right-1.5 top-full w-2 h-2 bg-gray-900 rotate-45 -translate-y-1" />
                  </div>
                </div>
              )}
              <input type="checkbox" className="hidden" checked={isChecked} onChange={() => toggle(opt.id, opt.select_all)} />
            </label>
          );
        })}
      </div>
    </div>
  );
};

const AudienceSelectField: React.FC<{
  field: BackendFormField; value: string | null; onChange: (v: string | null) => void;
}> = ({ field, value, onChange }) => {
  const options = field.options || [];

  if (options.length === 0) return (
    <div><FieldLabel field={field} /><p className="text-xs text-gray-400 italic">No options available.</p></div>
  );

  return (
    <div>
      <FieldLabel field={field} />
      <div className="space-y-1.5">
        {options.map(opt => {
          const isSelected = String(value) === String(opt.id);
          return (
            <label
              key={opt.id}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                isSelected ? 'border-[#07E5D1] bg-[#F0FDFA]' : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className={`w-4 h-4 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${
                isSelected ? 'border-[#07E5D1]' : 'border-gray-300'
              }`}>
                {isSelected && <div className="w-2 h-2 rounded-full bg-[#07E5D1]" />}
              </div>
              <span className={`text-sm flex-1 ${isSelected ? 'text-[#1C1C1E] font-medium' : 'text-gray-700'}`}>
                {opt.name}
              </span>
              <input type="radio" className="hidden" checked={isSelected} onChange={() => onChange(String(opt.id))} />
            </label>
          );
        })}
      </div>
    </div>
  );
};

// ── Concept card field ───────────────────────────────────────────────────────


const CONCEPT_FIELD_SCHEMA = {
  fields: [
    { name: 'productHeadline', type: 'str', description: 'Product name/headline (main title)', required: true },
    { name: 'brandName', type: 'str', description: 'Brand/manufacturer name', required: true },
    { name: 'consumerInsight', type: 'str', description: 'One line Description - problem this solves', required: false },
    { name: 'mainBenefit', type: 'str', description: 'Detailed Description - main value proposition', required: true },
    { name: 'reasonsToBelieve', type: 'list[str]', description: 'Reasons to believe - key claims', required: false },
    { name: 'pricePoint', type: 'str', description: 'Price & Pack info', required: false },
    { name: 'packSize', type: 'str', description: 'Pack size/quantity', required: false },
  ],
  context: 'consumer product concept test',
};


const ConceptCardField: React.FC<{
  field: BackendFormField;
  value: string;
  onChange: (v: string) => void;
}> = ({ field, value, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [showSample, setShowSample] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [pendingInitial, setPendingInitial] = useState('');

  React.useEffect(() => { injectVctStyles(); }, []);

  const processFile = async (file: File) => {
    const MAX_MB = 50;
    if (file.size > MAX_MB * 1024 * 1024) return;
    const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];
    if (!ALLOWED.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|webp|gif|heic|heif)$/i)) return;

    setParsing(true);

    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('field_schema', JSON.stringify(CONCEPT_FIELD_SCHEMA));
      const res = await fetch(`${API_URL}/parse-image`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error('parse failed');
      const data = await res.json();

      const parts: string[] = [];
      if (data.productHeadline) parts.push(`**Product Headline:** ${data.productHeadline}`);
      if (data.brandName) parts.push(`**Brand Name:** ${data.brandName}`);
      if (data.consumerInsight) parts.push(`**One Line Description:** ${data.consumerInsight}`);
      if (data.mainBenefit) parts.push(`**Detailed Description:** ${data.mainBenefit}`);
      const rtbs = (data.reasonsToBelieve || []).filter(Boolean);
      if (rtbs.length > 0) parts.push(`**Reasons to Believe:**\n${rtbs.map((r: string) => `- ${r}`).join('\n')}`);
      const pricePack = [data.pricePoint, data.packSize].filter(Boolean).join(' for ');
      if (pricePack) parts.push(`**Price & Pack:** ${pricePack}`);
      const conceptStr = parts.join('\n\n');
      setPendingInitial(conceptStr);
      setShowManual(true);
    } catch {
      const name = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
      const auto = `**Product Headline:** ${name}
**Brand Name:** Imported from upload
**One Line Description:** Auto-extracted from your concept card. Edit anything below.
**Detailed Description:** We pulled this from "${file.name}". Replace this paragraph with your full benefit story.

**Reasons to Believe:**
- Add your first claim
- Add your second claim
- Add your third claim

**Price & Pack:** $0.00 for 0 units`;
      setPendingInitial(auto);
      setShowManual(true);
    } finally {
      setParsing(false);
    }
  };

  const openManual = () => {
    setPendingInitial(value || CONCEPT_TEMPLATE);
    setShowManual(true);
  };

  return (
    <div>
      <FieldLabel field={field} />

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif"
        className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) processFile(f); e.target.value = ''; }}
      />

      {parsing ? (
        <div className="border border-gray-200 rounded-xl h-32 flex flex-col items-center justify-center gap-2 bg-gray-50">
          <Loader2 className="w-5 h-5 text-[#07E5D1] animate-spin" />
          <p className="text-xs text-gray-400">Extracting concept from image…</p>
        </div>
      ) : value ? (
        <div className="border border-[#07E5D1]/40 rounded-xl bg-[#E8FCFA]/40 px-4 py-3">
          <p className="text-xs text-gray-600 whitespace-pre-wrap line-clamp-4">{value}</p>
          <div className="flex gap-3 mt-2">
            <button type="button" onClick={openManual} className="text-xs text-[#07E5D1] hover:text-[#06C4B4] transition-colors">Edit</button>
            <button type="button" onClick={() => inputRef.current?.click()} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">Re-upload image</button>
          </div>
        </div>
      ) : (
        <>
          <div
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
              drag ? 'border-[#07E5D1] bg-[#E8FCFA]' : 'border-gray-300 bg-white hover:border-[#07E5D1] hover:bg-[#E8FCFA]/40'
            }`}
            onClick={() => inputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={e => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) processFile(f); }}
          >
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#E8FCFA] text-[#07E5D1] mb-3">
              <Upload className="w-5 h-5" />
            </div>
            <p className="text-sm font-medium text-gray-700 mb-1">
              Drop your concept card image or{' '}
              <span className="text-[#07E5D1] underline underline-offset-2">browse</span>
            </p>
            <p className="text-xs text-gray-400">PNG · JPG · WebP — up to 50MB</p>
          </div>
          <div className="flex items-center justify-between mt-2">
            <button type="button" onClick={openManual} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
              Write manually instead
            </button>
            <button type="button" onClick={() => setShowSample(true)} className="text-xs text-[#07E5D1] hover:text-[#06C4B4] transition-colors">
              See sample
            </button>
          </div>
        </>
      )}

      {showSample && <ConceptSampleModal onClose={() => setShowSample(false)} />}

      {showManual && (
        <ConceptManualModal
          initialValue={pendingInitial}
          onSave={(val) => { onChange(val); setShowManual(false); }}
          onClose={() => setShowManual(false)}
        />
      )}
    </div>
  );
};

// ── Canvas card (controlled) ──────────────────────────────────────────────────

interface CanvasCardProps {
  form: BackendForm;
  plan: BackendPlan;
  values: Record<string, FieldValue>;
  onChange: (id: string, v: FieldValue) => void;
  onSubmitStep: (formData: Record<string, any>) => void;
  onBack: (backToStepId: string) => void;
  isSubmitting: boolean;
  stepError: string | null;
}

const CanvasCard: React.FC<CanvasCardProps> = ({ form, plan, values, onChange, onSubmitStep, onBack, isSubmitting, stepError }) => {
  const prevStepId = form.step_number > 1
    ? plan.steps[form.step_number - 2]?.id ?? null
    : null;
  const handleSubmit = async () => {
    const formData: Record<string, any> = {};
    for (const f of form.fields) {
      const v = values[f.id];
      if (f.type === 'image_upload') {
        const slot = v as ImageSlot | null;
        formData[f.id] = slot ? await fileToBase64(slot.file) : null;
      } else if (f.type === 'image_list') {
        const slots = (v as ImageSlot[]) ?? [];
        formData[f.id] = await Promise.all(slots.map(s => fileToBase64(s.file)));
      } else {
        formData[f.id] = v;
      }
    }
    onSubmitStep(formData);
  };

  const isValid = form.fields.filter(f => f.required).every(f => {
    const v = values[f.id];
    if (v === undefined || v === null) return false;
    if (f.type === 'text_input' || f.type === 'textarea') return !!(v as string)?.trim();
    if (f.type === 'text_list') {
      const filled = ((v as string[]) ?? []).filter(r => (r as string).trim());
      return filled.length >= (f.min_count ?? 1);
    }
    if (f.type === 'image_upload') return v !== null;
    if (f.type === 'image_list') return ((v as ImageSlot[]) ?? []).length > 0;
    if (f.type === 'segment_select') return ((v as string[]) ?? []).length > 0;
    if (f.type === 'audience_select') return v != null && v !== '';
    if (f.type === 'concept_card') return !!(v as string)?.trim();
    return true;
  });

  return (
    <div className="border border-[#E5E7EB] rounded-xl bg-white shadow-sm overflow-hidden">
      <div className="px-4 py-2 border-b border-[#F1F5F9] flex items-center justify-between">
        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{form.title}</span>
        <span className="text-[11px] text-gray-400">Step {form.step_number} of {form.total_steps}</span>
      </div>
      <div className="px-4 pt-3 pb-2">
        {form.subtitle && <p className="text-xs text-gray-400 mb-4">{form.subtitle}</p>}
        {(() => {
          const imageUploadFields = form.fields.filter(f => f.type === 'image_upload');
          const multipleImageUploads = imageUploadFields.length > 1;
          const renderedImageRow = multipleImageUploads ? (
            <div key="__image_row__" className="flex gap-3">
              {imageUploadFields.map(field => (
                <div key={field.id} className="flex-1 min-w-0">
                  <ImageUploadField field={field} value={(values[field.id] as ImageSlot) || null} onChange={val => onChange(field.id, val)} />
                </div>
              ))}
            </div>
          ) : null;
          let imageRowInserted = false;
          const nodes = form.fields.map(field => {
            if (field.type === 'image_upload' && multipleImageUploads) {
              if (imageRowInserted) return null;
              imageRowInserted = true;
              return renderedImageRow;
            }
            const v = values[field.id];
            if (field.type === 'text_input') return <TextInputField key={field.id} field={field} value={(v as string) || ''} onChange={val => onChange(field.id, val)} />;
            if (field.type === 'textarea') return <TextareaField key={field.id} field={field} value={(v as string) || ''} onChange={val => onChange(field.id, val)} />;
            if (field.type === 'text_list') return <TextListField key={field.id} field={field} value={(v as string[]) || ['']} onChange={val => onChange(field.id, val)} />;
            if (field.type === 'image_list') return <ImageListField key={field.id} field={field} value={(v as ImageSlot[]) || []} onChange={val => onChange(field.id, val)} />;
            if (field.type === 'segment_select') return <SegmentSelectField key={field.id} field={field} value={(v as string[]) || []} onChange={val => onChange(field.id, val)} />;
            if (field.type === 'audience_select') return <AudienceSelectField key={field.id} field={field} value={(v as string) || null} onChange={val => onChange(field.id, val)} />;
            if (field.type === 'concept_card') return <ConceptCardField key={field.id} field={field} value={(v as string) || ''} onChange={val => onChange(field.id, val)} />;
            return null;
          });
          return <div className="space-y-5">{nodes}</div>;
        })()}
        {stepError && (
          <div className="mt-3 flex items-start gap-2 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            {stepError}
          </div>
        )}
      </div>
      <div className="px-4 py-3 border-t border-[#F1F5F9] flex items-center justify-between">
        {prevStepId ? (
          <button
            onClick={() => onBack(prevStepId)}
            disabled={isSubmitting}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>
        ) : <span />}
        <button
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
          className="flex items-center gap-1.5 text-xs font-semibold text-[#07E5D1] hover:text-[#06C4B4] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isSubmitting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : <>Next →</>}
        </button>
      </div>
    </div>
  );
};

// ── init helper ──────────────────────────────────────────────────────────────

function initValuesFromFields(fields: BackendFormField[]): Record<string, FieldValue> {
  const init: Record<string, FieldValue> = {};
  for (const f of fields) {
    if (f.type === 'text_input' || f.type === 'textarea') init[f.id] = (f.value as string) || '';
    else if (f.type === 'text_list') init[f.id] = Array.isArray(f.value) && f.value.length > 0 ? f.value : Array(f.initial_rows ?? 1).fill('');
    else if (f.type === 'image_upload') init[f.id] = null;
    else if (f.type === 'image_list') init[f.id] = [];
    else if (f.type === 'segment_select') init[f.id] = Array.isArray(f.value) ? f.value : [];
    else if (f.type === 'audience_select') init[f.id] = f.value != null ? String(f.value) : null;
    else if (f.type === 'concept_card') init[f.id] = (f.value as string) || '';
    else init[f.id] = null;
  }
  return init;
}

// ── Frozen plan summary (collapsible) ────────────────────────────────────────

export const FrozenPlanSummary: React.FC<{ plan: BackendPlan }> = ({ plan }) => {
  const doneSteps = plan.steps.filter(s => {
    if (s.status !== 'done') return false;
    if (s.collected_inputs) {
      return Object.values(s.collected_inputs).some(v => v != null && v !== '' && !(Array.isArray(v) && v.length === 0));
    }
    return s.value != null && s.value !== '';
  });

  if (doneSteps.length === 0) {
    return <p className="text-xs text-gray-400 italic mt-1">No input values available.</p>;
  }

  return (
    <div className="space-y-3">
      {doneSteps.map(step => {
        const HIDDEN_COLLECTED_KEYS = new Set(['audience_id', 'segment_ids']);
        const collectedEntries = step.collected_inputs
          ? Object.entries(step.collected_inputs).filter(([k, v]) => !HIDDEN_COLLECTED_KEYS.has(k) && v != null && v !== '' && !(Array.isArray(v) && v.length === 0))
          : null;
        return (
          <div key={step.id}>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">{step.label}</p>
            {collectedEntries
              ? <div className="space-y-0.5">{collectedEntries.map(([k, v]) => <StepValuePreview key={k} value={v} />)}</div>
              : <StepValuePreview value={step.value} />
            }
          </div>
        );
      })}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

interface ChatFormWidgetProps {
  sessionId: number;
  plan: BackendPlan;
  form: BackendForm | null;
  messageType: BackendMessageType;
  activeStepId?: string | null;
  isActive: boolean;
  onFormResponse: (response: FormResponse) => void;
}

const ChatFormWidget: React.FC<ChatFormWidgetProps> = ({
  sessionId, plan, form, messageType, activeStepId, isActive, onFormResponse,
}) => {
  const { user } = useAuth();
  const userDomain = user?.email?.split('@')[1]?.toLowerCase() ?? '';
  const isBacchusUser = !!userDomain && BACCHUS_ALLOWED_DOMAINS.includes(userDomain);

  const [values, setValues] = useState<Record<string, FieldValue>>(
    () => form ? initValuesFromFields(form.fields) : {}
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);

  // Re-init values when the step changes
  const prevStepIdRef = useRef(form?.step_id);
  React.useEffect(() => {
    if (form && form.step_id !== prevStepIdRef.current) {
      prevStepIdRef.current = form.step_id;
      setValues(initValuesFromFields(form.fields));
    }
  }, [form?.step_id, form?.fields]);

  const handleChange = useCallback((id: string, v: FieldValue) => {
    setValues(prev => ({ ...prev, [id]: v }));
  }, []);

  const postToFormEndpoint = useCallback(async (formData: Record<string, any>): Promise<FormResponse | null> => {
    try {
      const res = await fetch(`${API_URL}/bluepill-chat/sessions/${sessionId}/form`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form_data: formData }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).message || `HTTP ${res.status}`);
      }
      return await res.json() as FormResponse;
    } catch (e: any) {
      setStepError(e?.message || 'Something went wrong. Please try again.');
      return null;
    }
  }, [sessionId]);

  const handleSubmitStep = useCallback(async (formData: Record<string, any>) => {
    setIsSubmitting(true);
    setStepError(null);
    const response = await postToFormEndpoint(formData);
    setIsSubmitting(false);
    if (response) onFormResponse(response);
  }, [postToFormEndpoint, onFormResponse]);

  const handleDispatch = useCallback(async () => {
    setIsSubmitting(true);
    setStepError(null);
    const dispatchData: Record<string, any> = { _dispatch: true };
    if (plan.use_case === 'concept_test') {
      dispatchData.population = isBacchusUser ? 'population_mondelez_cookies' : 'population_vitamin_survey_enriched_copy';
      dispatchData.sample_size = isBacchusUser ? 71 : 126;
    }
    const response = await postToFormEndpoint(dispatchData);
    setIsSubmitting(false);
    if (response) onFormResponse(response);
  }, [postToFormEndpoint, onFormResponse, plan.use_case, isBacchusUser]);

  const handleBack = useCallback(async (backToStepId: string) => {
    setIsSubmitting(true);
    setStepError(null);
    const response = await postToFormEndpoint({ _back_to_step: backToStepId });
    setIsSubmitting(false);
    if (response) onFormResponse(response);
  }, [postToFormEndpoint, onFormResponse]);

  const activePreview = form ? { fields: form.fields, values } : undefined;

  // Frozen — nothing to render here, the outer collapsible header handles display
  if (!isActive) {
    return null;
  }

  return (
    <div>
      {/* Canvas — step input form */}
      {messageType === 'specialist_collecting' && form && (
        <CanvasCard
          form={form}
          plan={plan}
          values={values}
          onChange={handleChange}
          onSubmitStep={handleSubmitStep}
          onBack={handleBack}
          isSubmitting={isSubmitting}
          stepError={stepError}
        />
      )}

      {/* specialist_ready — all inputs collected, show start button */}
      {messageType === 'specialist_ready' && (
        <div>
          {stepError && (
            <div className="mb-2 flex items-start gap-2 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              {stepError}
            </div>
          )}
          <button
            onClick={handleDispatch}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-4 py-2 bg-[#07E5D1] hover:bg-[#06C4B4] text-white text-xs font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {isSubmitting
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Starting…</>
              : <><Play className="w-3.5 h-3.5" /> Start {plan.label}</>
            }
          </button>
        </div>
      )}

      {messageType === 'intent_confirmation' && (
        <p className="text-xs text-gray-400 mt-1 px-1">
          Tell me more or just start typing your first answer.
        </p>
      )}
    </div>
  );
};

export default ChatFormWidget;
